import { useCallback, useMemo, useReducer } from 'react'

import { differenceInMonths } from 'date-fns'

import {
  SUPPLY_DEMAND_MAP_DEFAULT_MONTHS,
  SupplyDemandMapMonths,
} from 'constants/EntityConstants/Reports'
import {
  DriverHomeLocationDto,
  MapCoordinateDto,
  useGetSupplyDemandCollectionCoordinatesLazyQuery,
  useGetSupplyDemandDeliveryCoordinatesLazyQuery,
  useGetSupplyDemandDriverLocationsLazyQuery,
} from 'generated/graphql'

export type DriverStatusKey =
  | 'currentActive'
  | 'currentInactive'
  | 'currentLapsed'
  | 'currentDormant'

export type DriverStatusState = Record<DriverStatusKey, boolean>

export interface DriverStatusOption {
  key: DriverStatusKey
  label: string
  sub: string
  min: number
  max: number | null
}

export const DRIVER_STATUS_OPTIONS: DriverStatusOption[] = [
  { key: 'currentActive', label: 'Current Active', sub: '(0 - 3 months)', min: 0, max: 3 },
  { key: 'currentInactive', label: 'Current Inactive', sub: '(3 - 6 months)', min: 3, max: 6 },
  { key: 'currentLapsed', label: 'Current Lapsed', sub: '(6 - 12 months)', min: 6, max: 12 },
  { key: 'currentDormant', label: 'Current Dormant', sub: '(> 12 months)', min: 12, max: null },
]

const CHUNK_SIZE_MONTHS = 1

function buildTimeChunks(
  totalMonths: number,
): Array<{ fromMonthsAgo: number; toMonthsAgo: number }> {
  const chunks: Array<{ fromMonthsAgo: number; toMonthsAgo: number }> = []
  for (let toMonthsAgo = 0; toMonthsAgo < totalMonths; toMonthsAgo += CHUNK_SIZE_MONTHS) {
    chunks.push({
      fromMonthsAgo: Math.min(toMonthsAgo + CHUNK_SIZE_MONTHS, totalMonths),
      toMonthsAgo,
    })
  }
  return chunks
}

type Progress = { completed: number; total: number }

type State = {
  months: SupplyDemandMapMonths
  hasLoaded: boolean
  loading: boolean
  lastUpdated: Date | null
  driverStatuses: DriverStatusState
  driverLocations: DriverHomeLocationDto[]
  collectionPoints: MapCoordinateDto[]
  deliveryPoints: MapCoordinateDto[]
  progress: Progress
}

type Action =
  | { type: 'SET_MONTHS'; months: SupplyDemandMapMonths }
  | { type: 'FETCH_START'; totalQueries: number }
  | { type: 'DRIVERS_LOADED'; driverLocations: DriverHomeLocationDto[] }
  | { type: 'COLLECTION_CHUNK_LOADED'; points: MapCoordinateDto[] }
  | { type: 'DELIVERY_CHUNK_LOADED'; points: MapCoordinateDto[] }
  | { type: 'QUERY_FAILED' }
  | { type: 'TOGGLE_STATUS'; key: DriverStatusKey }

const initialState: State = {
  months: SUPPLY_DEMAND_MAP_DEFAULT_MONTHS,
  hasLoaded: false,
  loading: false,
  lastUpdated: null,
  driverStatuses: {
    currentActive: true,
    currentInactive: false,
    currentLapsed: false,
    currentDormant: false,
  },
  driverLocations: [],
  collectionPoints: [],
  deliveryPoints: [],
  progress: { completed: 0, total: 0 },
}

function advanceProgress(state: State, updates: Partial<State>): State {
  const completed = state.progress.completed + 1
  const done = completed === state.progress.total
  return {
    ...state,
    ...updates,
    loading: !done,
    lastUpdated: done ? new Date() : state.lastUpdated,
    progress: { ...state.progress, completed },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_MONTHS':
      return { ...state, months: action.months }
    case 'FETCH_START':
      return {
        ...state,
        hasLoaded: true,
        loading: true,
        driverLocations: [],
        collectionPoints: [],
        deliveryPoints: [],
        progress: { completed: 0, total: action.totalQueries },
      }
    case 'DRIVERS_LOADED':
      return advanceProgress(state, { driverLocations: action.driverLocations })
    case 'COLLECTION_CHUNK_LOADED':
      return advanceProgress(state, {
        collectionPoints: [...state.collectionPoints, ...action.points],
      })
    case 'DELIVERY_CHUNK_LOADED':
      return advanceProgress(state, {
        deliveryPoints: [...state.deliveryPoints, ...action.points],
      })
    case 'QUERY_FAILED':
      return advanceProgress(state, {})
    case 'TOGGLE_STATUS':
      return {
        ...state,
        driverStatuses: {
          ...state.driverStatuses,
          [action.key]: !state.driverStatuses[action.key],
        },
      }
  }
}

export const useSupplyDemandMap = () => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const [fetchDrivers] = useGetSupplyDemandDriverLocationsLazyQuery({ fetchPolicy: 'network-only' })
  const [fetchCollection] = useGetSupplyDemandCollectionCoordinatesLazyQuery({
    fetchPolicy: 'network-only',
  })
  const [fetchDelivery] = useGetSupplyDemandDeliveryCoordinatesLazyQuery({
    fetchPolicy: 'network-only',
  })

  const fetchAllLayers = useCallback(
    async (totalMonths: SupplyDemandMapMonths) => {
      const chunks = buildTimeChunks(totalMonths)
      dispatch({ type: 'FETCH_START', totalQueries: 1 + chunks.length * 2 })

      try {
        const r = await fetchDrivers()
        dispatch({
          type: 'DRIVERS_LOADED',
          driverLocations: r.data?.supplyDemandDriverLocations ?? [],
        })
      } catch {
        dispatch({ type: 'QUERY_FAILED' })
      }

      for (const chunk of chunks) {
        try {
          const r = await fetchCollection({ variables: { input: chunk } })
          dispatch({
            type: 'COLLECTION_CHUNK_LOADED',
            points: r.data?.supplyDemandCollectionCoordinates ?? [],
          })
        } catch {
          dispatch({ type: 'QUERY_FAILED' })
        }
      }

      for (const chunk of chunks) {
        try {
          const r = await fetchDelivery({ variables: { input: chunk } })
          dispatch({
            type: 'DELIVERY_CHUNK_LOADED',
            points: r.data?.supplyDemandDeliveryCoordinates ?? [],
          })
        } catch {
          dispatch({ type: 'QUERY_FAILED' })
        }
      }
    },
    [fetchDrivers, fetchCollection, fetchDelivery],
  )

  const handleLoad = useCallback(() => {
    fetchAllLayers(state.months)
  }, [fetchAllLayers, state.months])

  const toggleStatus = useCallback(
    (key: DriverStatusKey) => dispatch({ type: 'TOGGLE_STATUS', key }),
    [],
  )

  const setMonths = useCallback(
    (months: SupplyDemandMapMonths) => dispatch({ type: 'SET_MONTHS', months }),
    [],
  )

  const filteredDriverLocations = useMemo(() => {
    const now = new Date()
    return state.driverLocations.filter((d: DriverHomeLocationDto) => {
      const ago = d.lastActivityAt
        ? differenceInMonths(now, new Date(d.lastActivityAt as string))
        : Infinity
      const option =
        DRIVER_STATUS_OPTIONS.find((o) => ago >= o.min && (o.max === null || ago < o.max)) ??
        DRIVER_STATUS_OPTIONS[DRIVER_STATUS_OPTIONS.length - 1]
      return state.driverStatuses[option.key]
    })
  }, [state.driverLocations, state.driverStatuses])

  return {
    months: state.months,
    setMonths,
    hasLoaded: state.hasLoaded,
    lastUpdated: state.lastUpdated,
    loading: state.loading,
    progress: state.progress,
    driverStatuses: state.driverStatuses,
    toggleStatus,
    collectionPoints: state.collectionPoints,
    deliveryPoints: state.deliveryPoints,
    filteredDriverLocations,
    handleLoad,
  }
}
